import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';

import appsTemplate from '~/assets/images/templates/apps.webp';
import couponTemplate from '~/assets/images/templates/coupon.webp';
import imagesTemplate from '~/assets/images/templates/images.webp';
import linksTemplate from '~/assets/images/templates/list-of-links.webp';
import pdfTemplate from '~/assets/images/templates/pdf.webp';
import socialTemplate from '~/assets/images/templates/social-media.webp';
import vcardTemplate from '~/assets/images/templates/v-card.webp';
import videoTemplate from '~/assets/images/templates/video.webp';
import websiteTemplate from '~/assets/images/templates/website.webp';
import { TextBox } from '~/components/atoms/text-box';
import { Icon } from '~/components/ui/icon';
import { Text } from '~/components/ui/text';
import { Title, TITLE_TYPE } from '~/components/ui/title';
import { DESKTOP_MIN_WIDTH, TABLET_MIN_WIDTH } from '~/constants/common';
import { COLORS } from '~/styles/colors';

import {
  StyledCheckIcon,
  StyledContainer,
  StyledCreateButton,
  StyledFeatureItem,
  StyledFeatureList,
  StyledMainContentWrapper,
  StyledOptionCard,
  StyledOptionIconWrapper,
  StyledOptionsWrapper,
  StyledPhonePreview,
  StyledRightBlock,
  StyledRightBlockItem,
  StyledSeparator,
  StyledTitleWrapper,
} from './styled';

const QR_OPTIONS = [
  { id: 'website', icon: 'World', title: 'labels:website' },
  { id: 'pdf', icon: 'Pdf', title: 'landing:footer.pdf' },
  { id: 'links', icon: 'Link', title: 'common:listOfLinks' },
  { id: 'vcard', icon: 'UserId', title: 'common:vCardPlus' },
  { id: 'images', icon: 'Photo', title: 'landing:footer.images' },
  { id: 'social', icon: 'SocialMedia', title: 'landing:footer.social' },
  { id: 'apps', icon: 'DeviceMobile', title: 'landing:footer.apps' },
  { id: 'coupon', icon: 'Coupon', title: 'createQrStep1:items.b8.name' },
  { id: 'video', icon: 'Video', title: 'createQrStep1:items.b9.name' },
];

const TEMPLATES = {
  website: websiteTemplate,
  pdf: pdfTemplate,
  links: linksTemplate,
  vcard: vcardTemplate,
  images: imagesTemplate,
  social: socialTemplate,
  apps: appsTemplate,
  coupon: couponTemplate,
  video: videoTemplate,
};

const FEATURE_CONTENT = {
  website: {
    title: 'labels:website',
    description: 'landing:possibilities.website.connect',
    features: ['landing:possibilities.website.provides', 'landing:possibilities.website.update'],
  },
  pdf: {
    title: 'landing:footer.pdf',
    description: 'landing:possibilities.pdf.from',
    features: ['landing:possibilities.pdf.customize', 'landing:possibilities.pdf.adjust'],
  },
  links: {
    title: 'common:listOfLinks',
    description: 'landing:possibilities.links.share',
    features: ['landing:possibilities.links.make', 'landing:possibilities.links.allows'],
  },
  vcard: {
    title: 'landing:footer.vcard',
    description: 'landing:possibilities.vcard.transform',
    features: ['landing:possibilities.vcard.customize', 'landing:possibilities.vcard.make'],
  },
  images: {
    title: 'landing:footer.images',
    description: 'landing:possibilities.images.share',
    features: ['landing:possibilities.images.showcase', 'landing:possibilities.images.update'],
  },
  social: {
    title: 'landing:footer.social',
    description: 'landing:possibilities.social.simplify',
    features: ['landing:possibilities.social.with', 'landing:possibilities.social.update'],
  },
  apps: {
    title: 'landing:footer.apps',
    description: 'landing:possibilities.apps.simplify',
    features: ['landing:possibilities.apps.enable', 'landing:possibilities.apps.update'],
  },
  coupon: {
    title: 'createQrStep1:items.b8.name',
    description: 'landing:possibilities.coupon.description',
    features: ['landing:possibilities.coupon.feature1', 'landing:possibilities.coupon.feature2'],
  },
  video: {
    title: 'createQrStep1:items.b9.name',
    description: 'landing:possibilities.video.description',
    features: ['landing:possibilities.video.feature1', 'landing:possibilities.video.feature2'],
  },
};

export default function Possibilities() {
  const { t } = useTranslation();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down(TABLET_MIN_WIDTH));
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const [selectedOption, setSelectedOption] = useState('website');

  return (
    <StyledContainer>
      <StyledTitleWrapper>
        <TextBox text={t('landing:possibilities.titleLeft')} />
        <Text variant={isMobile ? 'h2' : 'h1.5'}>{t('landing:possibilities.titleRight')}</Text>
      </StyledTitleWrapper>
      {!isDesktop && (
        <StyledRightBlockItem>
          <StyledOptionsWrapper>
            {QR_OPTIONS.map((option) => (
              <StyledOptionCard key={option.id}>
                <StyledOptionIconWrapper
                  $isSelected={selectedOption === option.id}
                  aria-label={t(option.title)}
                  onClick={() => setSelectedOption(option.id)}
                >
                  <Icon icon={option.icon} withParentColor={false} size={32} width={30} height={30} />
                </StyledOptionIconWrapper>
                <Text variant="t16Regular" sx={{ color: COLORS.grey400, textAlign: 'center' }}>
                  {t(option.title)}
                </Text>
              </StyledOptionCard>
            ))}
          </StyledOptionsWrapper>
        </StyledRightBlockItem>
      )}
      <StyledMainContentWrapper>
        <StyledPhonePreview>
          <img src={TEMPLATES[selectedOption]} alt={`${selectedOption} Preview`} />
        </StyledPhonePreview>
        <StyledRightBlock>
          {isDesktop && (
            <StyledRightBlockItem>
              <Title className="poss-title" type={TITLE_TYPE.h3}>
                1. {t('landing:possibilities.chooseType')}
              </Title>
              <StyledSeparator />
              <StyledOptionsWrapper>
                {QR_OPTIONS.map((option) => (
                  <StyledOptionCard key={option.id}>
                    <StyledOptionIconWrapper
                      $isSelected={selectedOption === option.id}
                      aria-label={t(option.title)}
                      onClick={() => setSelectedOption(option.id)}
                    >
                      <Icon icon={option.icon} withParentColor={false} size={32} width={30} height={30} />
                    </StyledOptionIconWrapper>
                    <Text variant="t16Regular" sx={{ color: COLORS.grey400, textAlign: 'center' }}>
                      {t(option.title)}
                    </Text>
                  </StyledOptionCard>
                ))}
              </StyledOptionsWrapper>
            </StyledRightBlockItem>
          )}

          <StyledRightBlockItem>
            <Title type={TITLE_TYPE.h3}>{t(FEATURE_CONTENT[selectedOption]?.title)}</Title>
            <StyledSeparator />
            <Text variant="t16Regular" sx={{ color: COLORS.grey400 }}>
              {t(FEATURE_CONTENT[selectedOption]?.description)}
            </Text>
            <StyledFeatureList>
              {FEATURE_CONTENT[selectedOption]?.features?.map((feature, index) => (
                <StyledFeatureItem key={index}>
                  <StyledCheckIcon icon="Check" withParentColor={false} size={20} />
                  <Text variant="t16Regular" sx={{ color: COLORS.grey400 }}>
                    {t(feature)}
                  </Text>
                </StyledFeatureItem>
              ))}
            </StyledFeatureList>
            <StyledCreateButton onClick={() => window.scrollTo({ top: 145, behavior: 'smooth' })}>
              {t('landing:possibilities.createQrCode')}
            </StyledCreateButton>
          </StyledRightBlockItem>
        </StyledRightBlock>
      </StyledMainContentWrapper>
    </StyledContainer>
  );
}
